import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { css } from "styled-components";

interface Fields {
    mapImage: string;
    mapAlt: string;
    heading: string;
    description: string;
}
type Props = WidgetProps & { fields: Fields };

export interface ServiceTerritoryStyles {
    wrapper?: InjectableCss;
    map?: InjectableCss;
    textColumn?: InjectableCss;
    heading?: InjectableCss;
    description?: InjectableCss;
}

export const serviceTerritoryStyles: ServiceTerritoryStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 48px;
            width: 100%;
            margin-top: 88px;
            margin-left: 20px;
            margin-right: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    // xs / sm: map on top, text below
                    css`
                        flex-direction: column;
                        align-items: stretch;
                        gap: 24px;
                        margin-top: 40px;
                        margin-left: 0px;
                    `,
                    css`
                        flex-direction: column;
                        align-items: stretch;
                        gap: 32px;
                        margin-top: 64px;
                    `,
                    // md: keep the row with a tighter gap
                    css`
                        gap: 32px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    map: {
        css: css`
            /* desktop: 708 x 618 */
            flex: 0 0 708px;
            width: 708px;
            max-width: 100%;
            img {
                display: block;
                width: 100%;
                height: auto;
                aspect-ratio: 708 / 618;
                object-fit: cover;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: none;
                        width: 100%;
                    `,
                    css`
                        flex: none;
                        width: 100%;
                    `,
                    css`
                        flex: 0 0 50%;
                        width: 50%;
                    `,
                    css`
                        flex: 0 0 55%;
                        width: 55%;
                    `,
                    css`
                        flex: 0 0 708px;
                        width: 708px;
                    `,
                ])}
        `,
    },
    textColumn: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
        `,
    },
    heading: {
        css: css`
            font-size: 32px;
            line-height: 40px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: ${COLORS.black};
            margin: 0 0 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 28px;
                        line-height: 36px;
                        margin-bottom: 12px;
                    `,
                    css`
                        font-size: 28px;
                        line-height: 36px;
                    `,
                    css`
                        font-size: 28px;
                        line-height: 36px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    description: {
        css: css`
            font-size: 18px;
            line-height: 28px;
            color: #262525;
            p {
                margin: 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 14px;
                        line-height: 22px;
                    `,
                    css`
                        font-size: 16px;
                        line-height: 24px;
                    `,
                    css`
                        font-size: 16px;
                        line-height: 24px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const ServiceTerritory = ({ fields }: Props) => {
    const styles = serviceTerritoryStyles;
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.map}>
                {fields.mapImage && <Img src={fields.mapImage} altText={fields.mapAlt || fields.heading} />}
            </StyledWrapper>
            <StyledWrapper {...styles.textColumn}>
                <Typography as="h2" {...styles.heading}>
                    {fields.heading}
                </Typography>
                <Typography as="div" {...styles.description} dangerouslySetInnerHTML={{ __html: fields.description }} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ServiceTerritory,
    definition: {
        group: "Common",
        displayName: "CEN Service Territory",
        fieldDefinitions: [
            { name: "mapImage", displayName: "Map image", editorTemplate: "ImagePickerField", defaultValue: "", fieldType: "General" },
            { name: "mapAlt", displayName: "Map alt text", editorTemplate: "TextField", defaultValue: "Sales territories map", fieldType: "Translatable" },
            { name: "heading", displayName: "Heading", editorTemplate: "TextField", defaultValue: "Our Service Territory", fieldType: "Translatable" },
            { name: "description", displayName: "Description", editorTemplate: "RichTextField", defaultValue: "", fieldType: "Translatable" },
        ],
    },
};

export default widgetModule;